import React from "react";
import { motion } from "framer-motion";
import {
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  DocumentCheckIcon,
  Cog6ToothIcon,
  EyeIcon,
  UsersIcon
} from "@heroicons/react/24/outline";

const TPRMPage = () => {
  const categories = [
    {
      icon: ExclamationTriangleIcon,
      name: "Issue Management",
      description: "The process begins with risk assessment and due diligence, ensuring clear terms.",
      gradient: "from-red-500 to-orange-500",
      features: ["Risk Assessment", "Issue Tracking", "Resolution Workflow", "Compliance Monitoring"]
    },
    {
      icon: MagnifyingGlassIcon,
      name: "Risk Domain Screening",
      description: "The assessment starts with financial analysis, ensuring third-party stability and budget compliance.",
      gradient: "from-brand to-brand-dark",
      features: ["Financial Analysis", "Security Assessment", "Compliance Check", "Performance Review"]
    },
    {
      icon: UserGroupIcon,
      name: "Third Party Onboarding",
      description: "Integrates new vendors, suppliers, or partners to ensure compliance collaboration.",
      gradient: "from-green-500 to-emerald-500",
      features: ["Vendor Integration", "Compliance Verification", "Documentation", "Access Setup"]
    },
    {
      icon: DocumentCheckIcon,
      name: "Pre-Contract Due Diligence",
      description: "Ensures a potential vendor is a reliable match for the organization.",
      gradient: "from-purple-500 to-violet-500",
      features: ["Background Checks", "Reference Verification", "Risk Evaluation", "Capability Assessment"]
    },
    {
      icon: EyeIcon,
      name: "Post-Contract Due Diligence",
      description: "A continuous process ensuring compliance, performance, monitoring through SLAs & KPIs.",
      gradient: "from-yellow-500 to-orange-500",
      features: ["SLA Monitoring", "KPI Tracking", "Performance Review", "Compliance Audits"]
    },
    {
      icon: Cog6ToothIcon,
      name: "Vendor Management",
      description: "Continuous monitoring ensures vendors meet obligations and performance standards.",
      gradient: "from-teal-500 to-cyan-500",
      features: ["Performance Tracking", "Relationship Management", "Contract Compliance", "Risk Mitigation"]
    }
  ];

  const benefits = [
    "AI automation reduces third-party risk exposure efficiently",
    "Strengthened oversight fosters secure, trusted partnerships",
    "Operational excellence drives seamless collaboration success"
  ];

  const features = [
    {
      icon: CpuChipIcon,
      title: "AI-Powered Risk Assessment",
      description: "Advanced artificial intelligence continuously evaluates and scores vendor risks across multiple domains automatically."
    },
    {
      icon: ShieldCheckIcon,
      title: "Comprehensive Security Monitoring",
      description: "Real-time security monitoring ensures all third-party partners maintain required security standards and compliance."
    },
    {
      icon: ChartBarIcon,
      title: "Performance Analytics",
      description: "Detailed analytics and reporting provide insights into vendor performance, compliance status, and risk trends."
    },
    {
      icon: UsersIcon,
      title: "Trusted Partnership Management",
      description: "Build and maintain secure, trusted relationships with third-party vendors through comprehensive oversight and management."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative px-6 pt-12 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
              <BuildingOfficeIcon className="w-5 h-5 text-cyan-400 mr-2" />
              <span className="text-sm font-medium text-cyan-200">Mitigating Risks Maximizing Trust</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Third-Party Risk
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent block">
                Management
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
              Leverage AI-powered automation to mitigate risks and strengthen trust across third-party partnerships. 
              Tanikkai ensures comprehensive oversight, operational efficiency, and reinforced security for resilient, 
              seamless collaborations.
            </p>

            <div className="flex items-center justify-center mb-12">
              <img 
                src="https://images.unsplash.com/photo-1580983558189-84200466afb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHRlY2hub2xvZ3l8ZW58MHx8fGJsdWV8MTc1NDkyNTc2OHww&ixlib=rb-4.1.0&q=85" 
                alt="AI-Powered Third-Party Risk Management Technology"
                className="max-w-2xl w-full h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0"></div>
                    <p className="text-gray-300 text-sm">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Background Elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative px-6 py-20 bg-gradient-to-r from-slate-900/50 to-blue-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">AI-Driven TPRM Solutions</h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Tanikkai AI integrates to assess and manage vendor risks, focusing on security, 
              compliance, and performance, ensuring alignment with regulatory standards.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:border-white/40 transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{feature.title}</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="relative px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">TPRM Categories</h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Comprehensive third-party risk management across all critical areas of vendor relationships 
              and partnership lifecycle management
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:border-white/40 transition-all duration-300"
              >
                <div className="flex items-center mb-6">
                  <div className={`w-14 h-14 bg-gradient-to-r ${category.gradient} rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform`}>
                    <category.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">
                    {category.name}
                  </h3>
                </div>
                
                <p className="text-gray-300 leading-relaxed text-sm mb-6">
                  {category.description}
                </p>

                {/* Feature Tags */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {category.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="bg-white/5 rounded-lg px-2 py-1 text-xs font-medium text-cyan-200 text-center border border-white/10"
                    >
                      {feature}
                    </div>
                  ))}
                </div>

                {/* AI-Powered Badge */}
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full border border-purple-400/30">
                    <CpuChipIcon className="w-4 h-4 text-purple-400 mr-1" />
                    <span className="text-xs font-medium text-purple-300">AI-Automated</span>
                  </span>
                  <span className="text-xs text-green-400 font-medium">Risk Optimized</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative px-6 py-20 bg-gradient-to-r from-slate-900/50 to-blue-900/30 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/20"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Secure Your Third-Party Ecosystem
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Transform your vendor relationships with AI-powered risk management and comprehensive oversight
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-2xl border-2 border-white/20"
              >
                Start Risk Assessment
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all border-2 border-white/30"
              >
                Schedule Consultation
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TPRMPage;